import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Link, useNavigate } from 'react-router-dom'
import { toMessage } from '@/api/problem'
import { FavoriteIcon, LogoutIcon, SettingsIcon, ShieldIcon, UserIcon } from '@/components/icons'
import { notifyError, notifySuccess } from '@/components/ui'
import { useAuth } from '@/stores/AuthProvider'
import { cn } from '@/utils/cn'

/**
 * 顶栏用户入口（设计稿右上角的圆角头像按钮）。
 *
 * 三种状态各有不同呈现，因为用户此刻能做的事不同：
 *   - 加载中：按钮存在但不可操作（会话还没确定，弹出「登录」再瞬间变成用户名很难看）；
 *   - 游客：点击直接去登录页，不弹菜单 —— 只有一个选项的菜单是多余的一步；
 *   - 已登录：弹菜单，含我的收藏、账号设置、退出登录。
 */
export function UserMenu({ className }: { className?: string }) {
  const { status, user, isAdmin, logout } = useAuth()
  const navigate = useNavigate()

  const triggerClass = cn(
    'inline-flex size-10 shrink-0 items-center justify-center rounded-card border border-border bg-surface-muted text-muted-foreground transition-colors hover:border-border-strong hover:text-foreground sm:size-11',
    className,
  )

  if (status === 'loading') {
    return (
      <span className={cn(triggerClass, 'animate-pulse')} aria-hidden="true">
        <UserIcon className="size-5" />
      </span>
    )
  }

  if (status === 'anonymous') {
    return (
      <Link to="/login" className={triggerClass} aria-label="登录或注册">
        <UserIcon className="size-5" />
      </Link>
    )
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger className={triggerClass} aria-label={`账号菜单：${user?.display_name}`}>
        <UserIcon className="size-5" />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          className="z-50 min-w-52 animate-slide-up overflow-hidden rounded-card border border-border bg-surface-raised p-1 shadow-overlay"
        >
          <div className="px-2.5 py-2">
            <p className="truncate text-sm font-semibold text-foreground">{user?.display_name}</p>
            <p className="truncate text-xs text-muted-foreground">@{user?.username}</p>
          </div>
          <DropdownMenu.Separator className="my-1 h-px bg-border" />

          <DropdownMenu.Item asChild>
            <Link
              to="/favorites"
              className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-foreground outline-none transition-colors data-[highlighted]:bg-surface-muted"
            >
              <FavoriteIcon className="size-4 text-muted-foreground" />
              我的收藏
            </Link>
          </DropdownMenu.Item>

          <DropdownMenu.Item asChild>
            <Link
              to="/settings/account"
              className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-foreground outline-none transition-colors data-[highlighted]:bg-surface-muted"
            >
              <SettingsIcon className="size-4 text-muted-foreground" />
              账号设置
            </Link>
          </DropdownMenu.Item>

          {/*
            管理后台入口。

            只对管理员渲染 —— 普通用户看到一个点进去是 404 的条目毫无意义，
            而且没必要向他们宣告后台的存在（AdminLayout 里有同一决定的完整说明）。
            真正的门在后端：59 个 /admin/** 接口每个都过 requireAdmin()。
          */}
          {isAdmin && (
            <DropdownMenu.Item asChild>
              <Link
                target="_blank"
                to="/admin"
                className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-foreground outline-none transition-colors data-[highlighted]:bg-surface-muted"
              >
                <ShieldIcon className="size-4 text-muted-foreground" />
                管理后台
              </Link>
            </DropdownMenu.Item>
          )}

          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          <DropdownMenu.Item
            onSelect={async () => {
              try {
                await logout()
                notifySuccess('已退出登录')
                navigate('/')
              } catch (error) {
                // logout 内部已经把本地状态清成游客，这里只需告知失败原因。
                notifyError(toMessage(error, '退出登录失败'))
              }
            }}
            className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-foreground outline-none transition-colors data-[highlighted]:bg-surface-muted"
          >
            <LogoutIcon className="size-4 text-muted-foreground" />
            退出登录
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
